import { CssBaseline, ThemeProvider, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { ReactNode, useLayoutEffect } from "react";
import { hideInitialLoadingScreen } from "contexts/globalLoadingScreen";
import theme from "src/theme";

export type ArtefactState = "loading" | "ready" | "error";

const toEmbeddedJson = (rawData: unknown): string =>
  // Escape `<` so the serialised data cannot terminate the surrounding
  // <script> element; `<` round-trips back to `<` through JSON.parse.
  JSON.stringify(rawData ?? null).replace(/</g, "\\u003c");

// The marker must be a visible element with text: the headless renderer waits
// for the marker to be *visible*, and a zero-height empty div never is — the
// capture would wait out its full timeout instead of failing fast. The text is
// the failure reason, which the renderer reads into its error.
const ArtefactError = ({ message }: { message: string }) => (
  <Typography data-artefact-error="true" sx={{ p: 2, fontFamily: "monospace" }}>
    {message}
  </Typography>
);

// Frame for a headless-render artefact route: once its data is ready, it
// exposes the raw data as JSON in a `data-artefact-raw-data` script and marks
// the render `data-artefact-ready` (or `data-artefact-error`), which a headless
// renderer waits on to read the data back out and screenshot.
export const ArtefactShell = ({
  state,
  rawData,
  errorMessage,
  children,
}: {
  state: ArtefactState;
  rawData: unknown;
  errorMessage?: string;
  children: ReactNode;
}) => {
  // The app shell pins #root to the full viewport height; the headless capture
  // clips to element bounds, so that would pad every shot to viewport height.
  useLayoutEffect(() => {
    const root = document.getElementById("root");
    if (root) {
      root.style.height = "auto";
    }
  }, []);

  // Drop the app's full-screen loading splash once ready, so it's not in the shot.
  useLayoutEffect(() => {
    if (state !== "loading") {
      hideInitialLoadingScreen({ immediate: true });
    }
  }, [state]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* A render-time throw in the children flips to the same error marker a
          query error uses, so the headless capture fails fast instead of
          hanging until the ready timeout. */}
      <Sentry.ErrorBoundary
        fallback={({ error }) => <ArtefactError message={String(error)} />}
      >
        {state === "error" && (
          <ArtefactError
            message={errorMessage ?? "artefact data query failed"}
          />
        )}
        {state === "ready" && (
          <>
            <script
              type="application/json"
              data-artefact-raw-data="true"
              // The only way to set a script element's text in React; the payload
              // is `<`-escaped JSON, so it cannot inject markup.
              // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
              dangerouslySetInnerHTML={{ __html: toEmbeddedJson(rawData) }}
            />
            <div data-artefact-ready="true">{children}</div>
          </>
        )}
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
};

// Standalone error page for the render routes' guards (e.g. a missing
// permission): reuses the shell so the loading splash is dropped and the
// headless renderer sees a visible error marker instead of a blank page it
// would wait out.
export const ArtefactErrorPage = ({ message }: { message: string }) => (
  <ArtefactShell state="error" rawData={null} errorMessage={message}>
    {null}
  </ArtefactShell>
);
